// ===========================================
// Parent Event Detail Page
// ===========================================
// Displays a single published event with RSVP controls and (if paid)
// a Stripe Checkout button.  Holder name/email are prefilled from the
// authenticated user via useAuth().

import { useState, lazy, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Stack,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Paper,
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  ConfirmationNumber as TicketIcon,
  People as PeopleIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  IosShare as ShareIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import TicketModal from '../../components/events/TicketModal';
import { useParentEvent, useSubmitRsvp, useTicketCheckout, useMyEventTickets } from '../../hooks/useEventsParent';
import { useAuth } from '../../contexts/useAuth';
import { useTenant } from '../../contexts/TenantContext';
import type {
  RSVPResponse,
  EventType,
  EventTicketTierSummary,
  TicketItemInput,
  MyEventTicket,
} from '../../api/events.api';
import { eventIcsByIdUrl, googleCalendarEventUrl } from '../../api/events.api';
import { EVENT_TYPE_LABELS } from '../../constants/eventTypes';
import { formatAddress } from '../../constants/countries';
import {
  TierQuantityPicker,
  tierPriceSummary,
  tierOrderTotalCents,
  quantitiesToItems,
  formatCents,
} from '../../components/events/EventTierPricing';

// Lazy-load the map so mapbox-gl stays out of the main bundle.
const VenueMap = lazy(() => import('../../components/events/VenueMap'));

// ===========================================
// HELPERS
// ===========================================

const RSVP_OPTIONS: { value: RSVPResponse; label: string }[] = [
  { value: 'ATTENDING', label: "Yes, I'm attending" },
  { value: 'NOT_ATTENDING', label: "No, I can't make it" },
  { value: 'MAYBE', label: 'Maybe' },
];

// ===========================================
// CHECKOUT PAYLOAD
// ===========================================

interface CheckoutPayload {
  items?: TicketItemInput[];
  quantity?: number;
  rsvpId?: string;
}

// ===========================================
// RSVP FORM (free events only)
// ===========================================

interface RsvpFormProps {
  eventId: string;
}

function RsvpForm({ eventId }: RsvpFormProps) {
  const [response, setResponse] = useState<RSVPResponse>('ATTENDING');
  const [guestCount, setGuestCount] = useState(1);
  const [notes, setNotes] = useState('');
  const [rsvpDone, setRsvpDone] = useState(false);

  const submitRsvp = useSubmitRsvp();

  const handleRsvpSubmit = async () => {
    await submitRsvp.mutateAsync({
      eventId,
      data: {
        response,
        guestCount,
        notes: notes.trim() || undefined,
      },
    });
    setRsvpDone(true);
  };

  if (rsvpDone) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CheckIcon color="success" />
          <Typography variant="body1" fontWeight={600}>
            RSVP submitted — {RSVP_OPTIONS.find((o) => o.value === response)?.label}
          </Typography>
        </Stack>
        <Button variant="text" size="small" onClick={() => setRsvpDone(false)}>
          Update RSVP
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Response */}
      <FormControl fullWidth size="small">
        <InputLabel id="rsvp-response-label">Your response</InputLabel>
        <Select
          labelId="rsvp-response-label"
          value={response}
          label="Your response"
          onChange={(e) => setResponse(e.target.value as RSVPResponse)}
        >
          {RSVP_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Guest count */}
      <FormControl fullWidth size="small">
        <InputLabel id="guest-count-label">Guests attending</InputLabel>
        <Select
          labelId="guest-count-label"
          value={guestCount}
          label="Guests attending"
          onChange={(e) => setGuestCount(Number(e.target.value))}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Notes */}
      <TextField
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        multiline
        rows={2}
        size="small"
        fullWidth
        placeholder="Dietary requirements, accessibility needs, etc."
      />

      <Button variant="contained" color="primary" onClick={handleRsvpSubmit} disabled={submitRsvp.isPending}>
        {submitRsvp.isPending ? 'Submitting…' : 'Submit RSVP'}
      </Button>
    </Stack>
  );
}

// ===========================================
// TICKET PURCHASE (paid events only)
// ===========================================

interface TicketPurchaseProps {
  ticketPriceCents: number | null;
  tiers: EventTicketTierSummary[];
  onCheckout: (payload: CheckoutPayload) => void;
  checkoutLoading: boolean;
  hasOwnedTickets: boolean;
}

function TicketPurchase({ ticketPriceCents, tiers, onCheckout, checkoutLoading, hasOwnedTickets }: TicketPurchaseProps) {
  // Per-tier quantities (tier path). Legacy single-price path uses quantity.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [quantity, setQuantity] = useState(1);

  const hasTiers = tiers.length > 0;
  const tierTotalQty = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
  const tierTotalCents = tierOrderTotalCents(tiers, quantities);
  const canBuyTiers = hasTiers && tierTotalQty >= 1 && tierTotalCents > 0;

  const legacyTotalCents = (ticketPriceCents ?? 0) * quantity;

  const handleBuy = () => {
    onCheckout(
      hasTiers ? { items: quantitiesToItems(quantities) } : { quantity }
    );
  };

  return (
    <Stack spacing={2}>
      {hasTiers ? (
        <TierQuantityPicker tiers={tiers} quantities={quantities} onChange={setQuantities} />
      ) : (
        <FormControl fullWidth size="small">
          <InputLabel id="ticket-qty-label">Tickets</InputLabel>
          <Select
            labelId="ticket-qty-label"
            value={quantity}
            label="Tickets"
            onChange={(e) => setQuantity(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
          {ticketPriceCents != null && (
            <FormHelperText>Total: {formatCents(legacyTotalCents)}</FormHelperText>
          )}
        </FormControl>
      )}

      <Button
        variant={hasOwnedTickets ? 'outlined' : 'contained'}
        color="primary"
        startIcon={<TicketIcon />}
        onClick={handleBuy}
        disabled={checkoutLoading || (hasTiers && !canBuyTiers)}
      >
        {checkoutLoading
          ? 'Redirecting…'
          : hasTiers
            ? `${hasOwnedTickets ? 'Buy more tickets' : 'Buy Tickets'} — ${formatCents(tierTotalCents)}`
            : `${hasOwnedTickets ? 'Buy more tickets' : `Buy Ticket${quantity > 1 ? `s (${quantity})` : ''}`} — ${formatCents(legacyTotalCents)}`}
      </Button>
    </Stack>
  );
}

// ===========================================
// MAIN PAGE
// ===========================================

export default function ParentEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { schoolSlug } = useTenant();

  const { data: queryData, isLoading, isError } = useParentEvent(id ?? '');
  const ticketCheckout = useTicketCheckout();
  const [searchParams] = useSearchParams();
  const ticketSuccess = searchParams.get('ticket') === 'success';

  // Fetch the parent's own tickets for this event (independent of the Stripe
  // redirect — works even when the ?ticket=success param is absent).
  // apiClient + the api method each strip one wrapper layer, so the hook
  // receives the bare MyEventTicket[].
  const { data: myTickets } = useMyEventTickets(id ?? '', !isLoading && !isError);
  const ownedTickets: MyEventTicket[] = myTickets ?? [];

  // Selected ticket for the "View ticket" QR display modal.
  const [selectedTicket, setSelectedTicket] = useState<MyEventTicket | null>(null);

  // apiClient + the api method each strip one wrapper layer, so the hook already
  // returns the bare Event (no further .data unwrap).
  type EventShape = import('../../api/events.api').Event;
  const event = queryData as EventShape | undefined;

  const holderName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const holderEmail = user?.email ?? '';

  const handleCheckout = (payload: CheckoutPayload) => {
    if (!event) return;
    const base = window.location.origin;
    const successUrl = schoolSlug
      ? `${base}/${schoolSlug}/parent/events/${event.id}?ticket=success`
      : `${base}/parent/events/${event.id}?ticket=success`;
    const cancelUrl = window.location.href;

    ticketCheckout.mutate(
      {
        eventId: event.id,
        data: {
          holderName,
          holderEmail,
          ...(payload.items ? { items: payload.items } : { quantity: payload.quantity }),
          rsvpId: payload.rsvpId ?? null,
          successUrl,
          cancelUrl,
        },
      },
      {
        onSuccess: (result) => {
          // Hook already returns the bare TicketCheckoutResult.
          const url = (result as { url?: string })?.url;
          if (url) window.location.href = url;
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !event) {
    return (
      <Box>
        <PageHeader
          title="Event"
          breadcrumbs={[
            { label: 'Dashboard', path: `/${schoolSlug}/parent` },
            { label: 'Events', path: `/${schoolSlug}/parent/events` },
            { label: 'Event' },
          ]}
        />
        <Alert severity="error">Event not found or could not be loaded.</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(schoolSlug ? `/${schoolSlug}/parent/events` : '/parent/events')}
          sx={{ mt: 2 }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  const startDate = new Date(event.startDateTime);
  const endDate = new Date(event.endDateTime);
  const isSameDay = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');

  const venueLine = [event.venueName, event.Location?.name, event.Room?.name]
    .filter(Boolean)
    .join(', ');

  const typeLabel = EVENT_TYPE_LABELS[event.type as EventType] ?? event.type;

  // Add-to-calendar links. ICS uses the by-id route (works for family-only
  // events, not just public). Google link is data-built (no fetch).
  const googleCalUrl = googleCalendarEventUrl({
    title: event.title,
    start: event.startDateTime,
    end: event.endDateTime,
    details: event.description,
    location: venueLine || null,
  });
  const icsUrl = eventIcsByIdUrl(schoolSlug || '', event.id);

  return (
    <Box>
      <PageHeader
        title={event.title}
        breadcrumbs={[
          { label: 'Dashboard', path: `/${schoolSlug}/parent` },
          { label: 'Events', path: `/${schoolSlug}/parent/events` },
          { label: event.title },
        ]}
      />

      {ticketSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Payment successful — your ticket has been issued. Check your email for confirmation.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main detail column */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ boxShadow: 'none' }}>
            {event.featuredImage && (
              <CardMedia
                component="img"
                height="240"
                image={event.featuredImage}
                alt={event.title}
                sx={{ objectFit: 'cover' }}
              />
            )}
            <CardContent>
              {/* Type + price chips */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={typeLabel}
                  size="small"
                  sx={{ backgroundColor: '#4580E4', color: '#fff', fontWeight: 600 }}
                />
                {event.isFree ? (
                  <Chip
                    label="Free"
                    size="small"
                    sx={{ backgroundColor: '#96DAC9', color: '#fff', fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    label={
                      tierPriceSummary(event.EventTicketTier) ??
                      `$${((event.ticketPriceCents ?? 0) / 100).toFixed(2)}`
                    }
                    size="small"
                    icon={<TicketIcon sx={{ fontSize: '0.9rem !important' }} />}
                    sx={{ backgroundColor: '#FFCE00', color: '#080808', fontWeight: 600 }}
                  />
                )}
              </Stack>

              {/* Date/time */}
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
                <EventIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {format(startDate, 'EEEE d MMMM yyyy')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(startDate, 'h:mm a')}
                    {isSameDay
                      ? ` – ${format(endDate, 'h:mm a')}`
                      : ` – ${format(endDate, 'EEEE d MMMM, h:mm a')}`}
                  </Typography>
                </Box>
              </Stack>

              {/* Venue */}
              {venueLine && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <LocationIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body1">{venueLine}</Typography>
                </Stack>
              )}

              {/* Venue map (only when a saved venue with coords is attached) */}
              {event.Venue && (
                <Box sx={{ mb: 2 }}>
                  <Suspense fallback={null}>
                    <VenueMap
                      latitude={event.Venue.latitude}
                      longitude={event.Venue.longitude}
                      name={event.Venue.name}
                      address={formatAddress(event.Venue)}
                    />
                  </Suspense>
                </Box>
              )}

              {/* School Location map (when the event uses a Location, not an
                  external Venue — one-shape rule means only one is ever set). */}
              {!event.Venue && event.Location && (
                <Box sx={{ mb: 2 }}>
                  <Suspense fallback={null}>
                    <VenueMap
                      latitude={event.Location.latitude}
                      longitude={event.Location.longitude}
                      name={event.Location.name}
                      address={formatAddress({ ...event.Location, country: event.Location.country ?? '' })}
                    />
                  </Suspense>
                </Box>
              )}

              {/* Add to calendar */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button
                  component="a"
                  href={googleCalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarIcon />}
                >
                  Google Calendar
                </Button>
                <Button
                  component="a"
                  href={icsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                >
                  Apple / Outlook (.ics)
                </Button>
              </Box>

              {/* Capacity */}
              {event.capacity != null && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <PeopleIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">
                    Capacity: {event.capacity}
                    {event._count?.EventRSVP != null ? ` · ${event._count.EventRSVP} RSVPs` : ''}
                  </Typography>
                </Stack>
              )}

              {/* Description */}
              {event.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                  >
                    {event.description}
                  </Typography>
                </>
              )}

              {/* Registration deadline */}
              {event.registrationDeadline && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Registration deadline:{' '}
                    {format(new Date(event.registrationDeadline), 'EEE d MMM yyyy, h:mm a')}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RSVP / ticket sidebar */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: { md: 'sticky' }, top: { md: 16 } }}>
            <Typography variant="h6" gutterBottom>
              {event.isFree ? 'RSVP' : 'Tickets'}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Owned-tickets panel — redirect-independent purchase confirmation.
                Shown for paid events when the parent already has PAID/CHECKED_IN
                tickets, regardless of whether the Stripe redirect returned. */}
            {!event.isFree && ownedTickets.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert
                  severity="success"
                  icon={<TicketIcon fontSize="inherit" />}
                  sx={{ mb: 1.5 }}
                >
                  You have {ownedTickets.length} ticket{ownedTickets.length > 1 ? 's' : ''} for this event.
                </Alert>
                <Stack spacing={1}>
                  {ownedTickets.map((ticket) => (
                    <Box
                      key={ticket.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={500}>
                          {ticket.EventTicketTier?.label ?? 'Ticket'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatCents(ticket.priceCents)}
                          </Typography>
                          {ticket.status === 'CHECKED_IN' && (
                            <Chip
                              label="Checked in"
                              size="small"
                              icon={<CheckIcon />}
                              sx={{ backgroundColor: '#96DAC9', color: '#080808', fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TicketIcon />}
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        View ticket
                      </Button>
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ mt: 2, mb: 2 }} />
              </Box>
            )}

            {event.status === 'CANCELLED' ? (
              <Alert severity="error">This event has been cancelled.</Alert>
            ) : event.isFree ? (
              <RsvpForm eventId={event.id} />
            ) : (
              <TicketPurchase
                ticketPriceCents={event.ticketPriceCents}
                tiers={event.EventTicketTier ?? []}
                onCheckout={handleCheckout}
                checkoutLoading={ticketCheckout.isPending}
                hasOwnedTickets={ownedTickets.length > 0}
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mt: 3 }}>
        Back to Events
      </Button>

      {selectedTicket && (
        <TicketModal
          open={!!selectedTicket}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
          eventTitle={event.title}
          eventDateLabel={`${format(startDate, 'EEEE d MMMM yyyy')}, ${format(startDate, 'h:mm a')}`}
          venueLine={venueLine}
        />
      )}
    </Box>
  );
}
